import React from 'react';
import {
  ShieldCheck,
  Award,
  Download,
  Loader2,
  Code2,
  Lock,
  Clock,
  Printer,
} from 'lucide-react';
import type { Book } from '../../../../shared/types/index.js';
import { BarcodeViewer } from '../../../../shared/components/BarcodeViewer.js';
import { QrCodeViewer } from '../../../../shared/components/QrCodeViewer.js';

interface BookDetailBarcodeSectionProps {
  book: Book;
  canEdit: boolean;
  activeCodeTab: 'barcode' | 'qr';
  isQuickDownloading: boolean;
  onCodeTabChange: (tab: 'barcode' | 'qr') => void;
  onOpenCertificateModal: () => void;
  onQuickDownloadCert: () => void;
  onOpenPrintLabel?: () => void;
  onOpenVerifyModal: () => void;
  onOpenEmbedBadgeModal: () => void;
}

export const BookDetailBarcodeSection: React.FC<BookDetailBarcodeSectionProps> = ({
  book,
  canEdit,
  activeCodeTab,
  isQuickDownloading,
  onCodeTabChange,
  onOpenCertificateModal,
  onQuickDownloadCert,
  onOpenPrintLabel,
  onOpenVerifyModal,
  onOpenEmbedBadgeModal,
}) => {
  const isApproved = book.status === 'disetujui';

  return (
    <div className="space-y-3">
      {/* 4a. Kode Identifikasi Resmi UIBN */}
      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-indigo-600" />
            <span className="text-xs font-bold text-slate-800 uppercase tracking-wide">
              Kode Identifikasi Resmi UIBN
            </span>
          </div>
          {isApproved && (
            /* Tab Barcode / QR */
            <div className="flex bg-slate-200/70 p-0.5 rounded-lg text-xs font-medium">
              <button
                type="button"
                onClick={() => onCodeTabChange('barcode')}
                className={`px-2.5 py-1 rounded-md transition cursor-pointer ${activeCodeTab === 'barcode' ? 'bg-white text-slate-900 shadow-xs font-bold' : 'text-slate-600 hover:text-slate-900'}`}
              >
                Barcode 1D
              </button>
              <button
                type="button"
                onClick={() => onCodeTabChange('qr')}
                className={`px-2.5 py-1 rounded-md transition cursor-pointer ${activeCodeTab === 'qr' ? 'bg-white text-slate-900 shadow-xs font-bold' : 'text-slate-600 hover:text-slate-900'}`}
              >
                QR Code 2D
              </button>
            </div>
          )}
        </div>

        {isApproved ? (
          activeCodeTab === 'barcode' ? (
            <BarcodeViewer
              uibn={book.uibn}
              bookTitle={book.title}
              showDownload={canEdit}
              onSwitchToQr={() => onCodeTabChange('qr')}
              className="w-full"
            />
          ) : (
            <QrCodeViewer uibn={book.uibn} bookTitle={book.title} showDownload={canEdit} size={150} className="w-full" />
          )
        ) : (
          <div className="p-4 rounded-xl bg-white border border-amber-200 text-center space-y-2">
            <div className="w-10 h-10 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 mx-auto">
              <Lock className="w-5 h-5" />
            </div>
            <h4 className="font-bold text-slate-900 text-xs sm:text-sm">
              Barcode & QR Code Belum Tersedia
            </h4>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Barcode 1D dan QR Code 2D resolusi tinggi untuk percetakan sampul serta verifikasi publik akan aktif dan dapat diunduh setelah pengajuan UIBN berstatus <strong>Disetujui</strong>.
            </p>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-slate-50 text-indigo-700 font-mono text-xs font-bold border border-slate-200">
              <span>UIBN Terjadwal: {book.uibn}</span>
            </div>
          </div>
        )}

        {isApproved && canEdit && (
          <div className="mt-2.5 px-3 py-2 bg-indigo-50/60 rounded-lg border border-indigo-100 flex items-start gap-2 text-[11px] text-indigo-900 leading-relaxed">
            <span className="font-bold shrink-0">💡 Tips Hemat Ruang:</span>
            <span>
              Gunakan <strong>QR Code 2D (Kotak)</strong> atau Barcode 1D ukuran <strong>Kompak</strong> agar tidak memakan banyak tempat pada sampul belakang atau punggung buku. Tersedia unduhan format <strong>SVG vektor</strong> (bebas pecah) dan <strong>PNG</strong>.
            </span>
          </div>
        )}

        {/* E-Certificate Quick Action Banner (Only available after approved) */}
        {canEdit && isApproved && (
            <div className="mt-3 p-3 rounded-xl bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-indigo-500/10 border border-amber-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-amber-500 text-slate-950 flex items-center justify-center shrink-0 shadow-xs">
                  <Award className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-bold text-slate-900 block">
                    Penerbitan E-Sertifikat Digital (PDF)
                  </span>
                  <span className="text-[11px] text-slate-500 block">
                    Format cetak resmi A4 Landscape dengan QR Code otentikasi & stempel kriptografis SHA-256.
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={onOpenCertificateModal}
                  className="px-3 py-1.5 rounded-lg bg-white border border-amber-300 hover:bg-amber-50 text-slate-800 text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer shadow-2xs"
                >
                  <Award className="w-3.5 h-3.5 text-amber-600" />
                  <span>Pratinjau Sertifikat</span>
                </button>
                <button
                  type="button"
                  onClick={onQuickDownloadCert}
                  disabled={isQuickDownloading}
                  className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-950 text-xs font-bold flex items-center gap-1.5 transition cursor-pointer shadow-xs disabled:opacity-50"
                  title="Unduh Berkas PDF Sertifikat"
                >
                  {isQuickDownloading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Download className="w-3.5 h-3.5" />
                  )}
                  <span>{isQuickDownloading ? 'Mengunduh...' : 'Unduh PDF'}</span>
                </button>
              </div>
            </div>
          )}

          {/* Book Print Labels Banner (Printer-friendly labels with QR & Barcode) */}
          {canEdit && isApproved && onOpenPrintLabel && (
            <div className="mt-2.5 p-3 rounded-xl bg-gradient-to-r from-indigo-500/10 via-sky-500/5 to-indigo-500/10 border border-indigo-200/90 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center shrink-0 shadow-xs">
                  <Printer className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-bold text-slate-900 block">
                    Cetak Label Buku & Stiker Barcode UIBN
                  </span>
                  <span className="text-[11px] text-slate-500 block">
                    Format stiker siap cetak berisi Barcode 1D, QR Code 2D, dan metadata buku untuk punggung/sampul.
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  id="btn-open-print-labels-banner"
                  onClick={onOpenPrintLabel}
                  className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold flex items-center gap-1.5 transition cursor-pointer shadow-xs"
                >
                  <Printer className="w-3.5 h-3.5" />
                  <span>Cetak Label Buku</span>
                </button>
              </div>
            </div>
          )}
        </div>

      {/* Embeddable Verification Badge Banner (Only available after approved) */}
      {canEdit && isApproved && (
        <div className="p-3 rounded-xl bg-gradient-to-r from-emerald-500/10 via-teal-500/5 to-indigo-500/10 border border-emerald-300/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0 shadow-xs">
              <Code2 className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-bold text-slate-900 block">
                  Widget Verifikasi Tersemat (Embeddable Badge)
                </span>
                <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                  HTML/JS
                </span>
              </div>
              <span className="text-[11px] text-slate-600 block">
                Sematkan lencana <em>"Buku ini terdaftar resmi di UIBN"</em> pada web penerbit/toko buku daring.
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              id="btn-open-verify-modal-detail"
              onClick={onOpenVerifyModal}
              className="px-3 py-1.5 rounded-lg bg-white border border-emerald-300 hover:bg-emerald-50 text-slate-800 text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer shadow-2xs"
            >
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              <span>Periksa Status</span>
            </button>

            <button
              type="button"
              id="btn-open-embed-badge-modal"
              onClick={onOpenEmbedBadgeModal}
              className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1.5 transition cursor-pointer shadow-xs"
            >
              <Code2 className="w-3.5 h-3.5" />
              <span>Dapatkan Widget</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
